/**
 * Determines how a payment URL should be opened based on provider.
 *
 * Providers that set restrictive CSP headers (e.g. `frame-ancestors 'self'`)
 * cannot be embedded in an iframe on our domain. For those providers we open
 * the payment page in an external browser tab instead.
 */

const EXTERNAL_TAB_PROVIDERS = new Set(['linkmepay', 'ggusonepay']);

export type PaymentUrlStrategy = 'iframe' | 'external_tab';

/**
 * Returns the appropriate strategy for displaying a payment URL.
 *
 * @param provider - The payment provider identifier (e.g. 'linkmepay', 'zeroxprocessing')
 * @returns 'external_tab' if the provider's payment page cannot be iframed, 'iframe' otherwise
 */
export function getPaymentUrlStrategy(provider: string | undefined | null): PaymentUrlStrategy {
  if (!provider) return 'iframe';
  return EXTERNAL_TAB_PROVIDERS.has(provider) ? 'external_tab' : 'iframe';
}

/**
 * Returns true if the payment URL should be opened in a new browser tab
 * instead of being embedded in an iframe.
 */
export function shouldOpenInExternalTab(provider: string | undefined | null): boolean {
  return getPaymentUrlStrategy(provider) === 'external_tab';
}

/** Resolved value of a design token on :root ('' when unavailable, e.g. outside a browser). */
function readCssToken(name: string): string {
  try {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  } catch {
    return '';
  }
}

/**
 * Opens a blank placeholder tab. MUST be called synchronously inside a user
 * gesture handler (before any `await`): mobile browsers block `window.open`
 * that happens after an async call, so the tab has to be opened while the
 * click is still "trusted", then redirected once the payment URL is known.
 *
 * Returns null when the popup was blocked anyway (caller should fall back to
 * the manual "Re-open payment page" button).
 */
export function openPlaceholderPaymentTab(): Window | null {
  const win = window.open('', '_blank');
  if (win) {
    try {
      win.opener = null;
      // The new tab has none of the app's stylesheets: copy the resolved token values across.
      const background = readCssToken('--k-base');
      const text = readCssToken('--k-text-secondary');
      win.document.write(
        '<!doctype html><title>Loading payment…</title>' +
        '<body style="margin:0;display:flex;align-items:center;justify-content:center;height:100vh;' +
        `background:${background};color:${text};font-family:system-ui,sans-serif">` +
        '<p>Loading payment page…</p></body>'
      );
    } catch {
      // Ignore: placeholder styling is best-effort only.
    }
  }
  return win;
}

/**
 * Redirects a placeholder tab to the payment URL. Falls back to a fresh
 * `window.open` if the placeholder is missing or was closed by the user.
 * Returns true if a tab is now showing the payment page.
 */
export function navigatePaymentTab(placeholder: Window | null, paymentUrl: string): boolean {
  if (placeholder && !placeholder.closed) {
    try {
      placeholder.location.href = paymentUrl;
      return true;
    } catch {
      // Fall through to a fresh window.open below.
    }
  }
  return window.open(paymentUrl, '_blank', 'noopener,noreferrer') != null;
}

/**
 * Closes a placeholder tab that ended up unused (payment failed, or the flow
 * produced a QR code instead of a URL).
 */
export function closePlaceholderPaymentTab(placeholder: Window | null): void {
  if (placeholder && !placeholder.closed) {
    try {
      placeholder.close();
    } catch {
      // Ignore: nothing else we can do.
    }
  }
}
